import type { ReactNode } from "react";
import { Icon } from "./Icon";

interface EmptyStateProps {
  // Allow either a built-in icon name or a fully custom node (used by pages
  // that already ship a domain-specific glyph in their fixtures).
  icon?: Parameters<typeof Icon>[0]["name"];
  title: string;
  description?: string;
  primary?: { label: string; onClick?: () => void; href?: string; testId?: string; disabled?: boolean };
  secondary?: { label: string; onClick?: () => void; href?: string; testId?: string };
  // Slot for richer custom illustrations / inline help links.
  extra?: ReactNode;
  testId?: string;
}

// Generic empty-state block used across chatbots / sites / knowledge / leads
// / conversations / audit-logs lists. Keeps zero-data UX consistent and gives
// users a clear next step (primary CTA) plus an optional learn-more link.
export function EmptyState({
  icon = "search",
  title,
  description,
  primary,
  secondary,
  extra,
  testId,
}: EmptyStateProps): JSX.Element {
  return (
    <div className="empty-state" data-testid={testId ?? "empty-state"}>
      <div className="empty-icon" aria-hidden="true">
        <Icon name={icon} size={22} />
      </div>
      <h4>{title}</h4>
      {description ? <p>{description}</p> : null}
      {extra}
      {primary || secondary ? (
        <div className="empty-actions">
          {primary ? (
            primary.href ? (
              <a
                className="btn btn-accent btn-sm"
                href={primary.href}
                data-testid={primary.testId}
              >
                {primary.label}
              </a>
            ) : (
              <button
                type="button"
                className="btn btn-accent btn-sm"
                onClick={primary.onClick}
                disabled={primary.disabled}
                data-testid={primary.testId}
              >
                {primary.label}
              </button>
            )
          ) : null}
          {secondary ? (
            secondary.href ? (
              <a
                className="btn btn-outline btn-sm"
                href={secondary.href}
                data-testid={secondary.testId}
              >
                {secondary.label}
              </a>
            ) : (
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={secondary.onClick}
                data-testid={secondary.testId}
              >
                {secondary.label}
              </button>
            )
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
